import type { ShareInstruction, ShareOwnershipType } from './types';

/**
 * The fields one ownership answer writes.
 *
 * Structurally the same shape `ShareInstructionSection` commits and
 * `shareFieldsPatch` turns into a patch — declared here rather than imported
 * from either, so this module stays the rule and neither call site owns it.
 */
export interface ShareOwnershipChange {
  ownershipType: ShareOwnershipType;
  isNonEstate: boolean;
  instruction?: ShareInstruction | null;
  transferBeneficiaries?: never[];
}

/**
 * **What changing a holding's ownership type must write** — the whole rule, in
 * one place (VALE-863).
 *
 * Ownership is not a lone field: `joint-tenants` means the shares pass to the
 * survivor, so it forces the instruction *and* the non-estate flag with it, and
 * `solely-held` has to undo that forcing rather than leave a surviving-joint
 * instruction behind on an estate asset.
 *
 * It lived inside the instruction editor's own `handleOwnershipChange`, which
 * was fine while that editor was the only way to answer. Tom's 27 Aug review
 * moved the question out of `INSTRUCTIONS` — ownership is a fact about what the
 * asset *is*, not a decision about what to do with it — and the moment there are
 * two ways to answer it, a rule living in one of them is a rule the other gets
 * wrong. Both now call this.
 *
 * Pure, and takes the current instruction rather than reading it, so the
 * attribute row can call it from a `toPatch` with no hook context.
 */
export function shareOwnershipChange(
  next: ShareOwnershipType,
  currentInstruction: ShareInstruction | null | undefined
): ShareOwnershipChange {
  const isNonEstate = next === 'joint-tenants';

  // Ownership and the instruction it forces travel together — the jointly-held
  // branch used to fire three un-awaited commits before LF-6374 made it one.
  if (
    next === 'joint-tenants' &&
    currentInstruction !== 'surviving-joint-holder'
  ) {
    return {
      ownershipType: next,
      isNonEstate,
      instruction: 'surviving-joint-holder',
      transferBeneficiaries: []
    };
  }

  // Coming back off jointly-held: the instruction it forced has to go with it,
  // or an estate asset keeps an instruction that only makes sense for one that
  // passed outside the estate.
  if (
    next === 'solely-held' &&
    currentInstruction === 'surviving-joint-holder'
  ) {
    return { ownershipType: next, isNonEstate, instruction: null };
  }

  return { ownershipType: next, isNonEstate };
}
